import { Granularity } from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import { formatWzId } from 'src/app/shared/utils/format-wz-id';
import { resolveSlicePreviewGranularity } from 'src/app/state/library/library.util';

export type PreviewCategoryPair = { code: string; name: string };

/** Category keys per dashboard granularity (series axis of the analysis). */
export const GRANULARITY_CATEGORY_PAIRS: Partial<
  Record<Granularity, PreviewCategoryPair[]>
> = {
  [Granularity.States]: [
    { code: 'state_code', name: 'state_name' },
    { code: 'state_id', name: 'state_name' },
  ],
  [Granularity.Districts]: [
    { code: 'district_id', name: 'district_name' },
    { code: 'administrative_district_id', name: 'administrative_district_name' },
  ],
  [Granularity.SubMarkets]: [
    { code: 'sub_market_id', name: 'sub_market_name' },
    { code: 'sub_market', name: 'sub_market_name' },
  ],
  [Granularity.IndustrySubMarkets]: [
    { code: 'sub_market_id', name: 'sub_market_name' },
    { code: 'sub_market', name: 'sub_market_name' },
  ],
  [Granularity.WzClasses]: [
    { code: 'wz_id', name: 'wz_name' },
    { code: 'wz_class_id', name: 'wz_class_name' },
    { code: 'wz_class', name: 'wz_name' },
  ],
};

/** Fallback candidates when granularity cannot be detected. */
export const CATEGORY_KEY_PAIRS: PreviewCategoryPair[] = [
  { code: 'state_code', name: 'state_name' },
  { code: 'state_id', name: 'state_name' },
  { code: 'sub_market_id', name: 'sub_market_name' },
  { code: 'sub_market', name: 'sub_market_name' },
  { code: 'wz_id', name: 'wz_name' },
  { code: 'wz_class_id', name: 'wz_class_name' },
  { code: 'wz_class', name: 'wz_name' },
  { code: 'district_id', name: 'district_name' },
  { code: 'administrative_district_id', name: 'administrative_district_name' },
];

/** Candidate keys for the time axis of a data row. */
export const TIME_KEYS = ['year', 'period_id', 'period'];

/** Candidate keys for the measure value of a data row. */
export const VALUE_KEYS = [
  'value',
  /** Economic-participants forecast columns keep the raw measure name. */
  'economic_participants_core_market',
  'value_count',
  'value_sales',
  /** Legacy GVA industries slices bound the raw column instead of `value`. */
  'gross_value_added__k_eur',
];

export const MAX_PREVIEW_SERIES = 16;
export const MAX_PREVIEW_VALUES = 16;

/** Pick the series axis from effective preview granularity. */
export function resolveCategoryPair(
  sample: Record<string, unknown>,
  dashboardId: string,
  shellId?: string,
): PreviewCategoryPair | undefined {
  const granularityId = resolveSlicePreviewGranularity(dashboardId, shellId);
  const candidates = GRANULARITY_CATEGORY_PAIRS[granularityId];
  const match = candidates?.find((pair) => pair.code in sample);
  if (match) {
    return match;
  }
  return CATEGORY_KEY_PAIRS.find((pair) => pair.code in sample);
}

/**
 * True when a selected filter option is stored in k€
 * (`revenue_per_company__k_eur`, GVA index, …). Chart-row then shows euros (×1000).
 */
export function hasKEurFilterValue(
  filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
): boolean {
  if (!filterValues) {
    return false;
  }
  const values = filterValues instanceof Map ? filterValues.values() : Object.values(filterValues);
  for (const value of values) {
    if (typeof value === 'string' && value.includes('k_eur')) {
      return true;
    }
  }
  return false;
}

/** Resolve value key: known candidate or first numeric column. */
export function pickValueKey(
  sample: Record<string, unknown>,
  categoryPair: PreviewCategoryPair | undefined,
  timeKey: string | undefined,
): string | null {
  const knownValueKey = VALUE_KEYS.find((key) => key in sample);
  if (knownValueKey) {
    return knownValueKey;
  }

  const reservedKeys = new Set<string>([
    ...(categoryPair ? [categoryPair.code, categoryPair.name] : []),
    ...(timeKey ? [timeKey] : []),
  ]);

  for (const key of Object.keys(sample)) {
    if (reservedKeys.has(key)) {
      continue;
    }
    if (Number.isFinite(Number(sample[key]))) {
      return key;
    }
  }
  return null;
}

export function readFilterValue(
  filterValues: Map<string, unknown> | Record<string, unknown> | undefined,
  key: string,
): unknown {
  if (!filterValues) {
    return undefined;
  }
  if (filterValues instanceof Map) {
    return filterValues.get(key);
  }
  return filterValues[key];
}

/**
 * Anonymized / missing measure for stack & slope previews.
 * Duel intentionally treats empty as numeric 0 — do not reuse there.
 */
export function isAnonPreviewCell(raw: unknown): boolean {
  const asStr = raw == null ? '' : `${raw}`.trim();
  if (asStr === '*' || asStr === '–' || asStr === '-' || asStr === '') {
    return true;
  }
  return !Number.isFinite(Number(raw));
}

/** Display label for a category code (WZ ids get formatted prefixes). */
export function formatCategoryLabel(
  row: Record<string, unknown>,
  categoryPair: PreviewCategoryPair,
  code: string,
): string {
  let name = `${row[categoryPair.name] ?? code}`;
  if (categoryPair.code === 'wz_id' || categoryPair.code === 'wz_class_id') {
    const wzLabel = formatWzId(code);
    const wzName = row[categoryPair.name];
    name = wzName ? `${wzLabel} ${wzName}` : wzLabel || name;
  }
  return name;
}
